import { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ApiUrl from "../Environment/ApiUrl";
import "../../assets/css/Loader.css";
import { useToast } from "../Toast/ToastContext";

function WhitePageBalance() {
  const [activeTab, setActiveTab] = useState("sales-balance");
  const [responseDataRetailer, setResponseDataRetailer] = useState([]);
  const [responseDataSales, setResponseDataSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const toast = useToast();

  // ── Date helpers ──────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day   = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}-${month}-${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    try {
      const date  = new Date(dateString);
      const today = new Date();
      return (
        date.getDate()     === today.getDate()     &&
        date.getMonth()    === today.getMonth()    &&
        date.getFullYear() === today.getFullYear()
      );
    } catch {
      return false;
    }
  };

  // ── Return-type aware formatters (mirrors BluePageBalance) ────────────────
  const fmtTotal = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.amount      || 0).toFixed(2)}`
      : `${parseFloat(item.total_pure   || 0).toFixed(3)} g`;

  const fmtPaid = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.enter_amount || 0).toFixed(2)}`
      : `${parseFloat(item.pure          || 0).toFixed(3)} g`;

  const fmtBalance = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.close || 0).toFixed(2)}`
      : `${parseFloat(item.close || 0).toFixed(3)} g`;

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDataRetailer();
    fetchDataSales();
  }, []);

  const fetchDataRetailer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ApiUrl}/Api/retailer_details.php`);
      const filteredData = (response.data.data || []).filter(
        (item) => item.close !== "0" && isToday(item.retailer_date)
      );
      setTimeout(() => setLoading(false), 1000);
      setResponseDataRetailer(filteredData);
    } catch (error) {
      console.error("Error fetching retailer data:", error.message);
      setResponseDataRetailer([]);
    }
  };

  const fetchDataSales = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/sales.php?date=today`);
      const filteredData = (response.data.data || []).filter(
        (item) =>
          item.balance !== "0" &&
          item.balance !== "0.00" &&
          isToday(item.sales_date)
      );
      setResponseDataSales(filteredData);
    } catch (error) {
      console.error("Error fetching sales data:", error.message);
      setResponseDataSales([]);
    }
  };

  // ── Tab / page helpers ────────────────────────────────────────────────────
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredDataRetailer = responseDataRetailer.filter(
    (item) =>
      item.retailer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDate(item.retailer_date)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.return_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.retailer_details?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.pure?.toString().includes(searchTerm) ||
      item.close?.toString().includes(searchTerm)
  );

  const filteredDataSales = responseDataSales.filter(
    (item) =>
      item.sales_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDate(item.sales_date)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_list[0]?.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_list[0]?.item?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.customer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.total?.toString().includes(searchTerm) ||
      item.balance?.toString().includes(searchTerm)
  );

  // ── Separate retailer totals by return_type ───────────────────────────────
  const amountRows = filteredDataRetailer.filter((r) => r.return_type === "Amount");
  const pureRows   = filteredDataRetailer.filter((r) => r.return_type === "Pure");

  const totalAmtValue   = amountRows.reduce((s, r) => s + parseFloat(r.amount       || 0), 0).toFixed(2);
  const totalAmtPaid    = amountRows.reduce((s, r) => s + parseFloat(r.enter_amount  || 0), 0).toFixed(2);
  const totalAmtBalance = amountRows.reduce((s, r) => s + parseFloat(r.close         || 0), 0).toFixed(2);
  const totalPureWt     = pureRows.reduce((s, r)   => s + parseFloat(r.total_pure    || 0), 0).toFixed(3);
  const totalPureRet    = pureRows.reduce((s, r)   => s + parseFloat(r.pure          || 0), 0).toFixed(3);
  const totalPureBal    = pureRows.reduce((s, r)   => s + parseFloat(r.close         || 0), 0).toFixed(3);

  // ── Pagination ────────────────────────────────────────────────────────────
  const dataToDisplay = activeTab === "retailers-balance" ? filteredDataRetailer : filteredDataSales;
  const totalPages    = Math.ceil(dataToDisplay.length / pageSize);
  const paginatedData = dataToDisplay.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const max   = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end   = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);

    if (start > 1) {
      pages.push(<li key={1} className={`page-item ${currentPage === 1 ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>);
      if (start > 2) pages.push(<li key="s…" className="page-item disabled"><span className="page-link">…</span></li>);
    }
    for (let i = start; i <= end; i++)
      pages.push(<li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(i)}>{i}</button></li>);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<li key="e…" className="page-item disabled"><span className="page-link">…</span></li>);
      pages.push(<li key={totalPages} className={`page-item ${currentPage === totalPages ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button></li>);
    }
    return pages;
  };

  // ── Export helpers ────────────────────────────────────────────────────────
  const retailerExportHeaders = [
    "S.No", "Retailer No", "Retailer ID", "Date", "Retailer Name",
    "Items", "Return Type", "Total / Total Pure", "Paid / Returned", "Balance",
  ];

  const retailerExportRows = (data) =>
    data.map((item, i) => [
      i + 1,
      item.retailer_id   || "",
      item.supplier_id   || "",
      formatDate(item.retailer_date),
      item.retailer_details?.name || "-",
      item.items?.length || 0,
      item.return_type   || "",
      fmtTotal(item),
      fmtPaid(item),
      fmtBalance(item),
    ]);

  const salesExportHeaders = ["S.No", "Sales ID", "Sales Date", "Category", "Customer ID", "Total", "Paid", "Balance"];
  const salesExportRows    = (data) =>
    data.map((item, i) => [
      i + 1,
      item.sales_no,
      formatDate(item.sales_date),
      item.item_list[0]?.category || "",
      item.customer_id,
      item.total,
      item.paid,
      item.balance,
    ]);

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    let title, tableHtml;

    if (activeTab === "retailers-balance") {
      title = "Retailers Balance Report";
      tableHtml = `<h2>${title}</h2>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr style="background:#f2f2f2">
            ${retailerExportHeaders.map((h) => `<th style="border:1px solid #ddd;padding:6px">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${retailerExportRows(filteredDataRetailer).map((row) =>
              `<tr>${row.map((c) => `<td style="border:1px solid #ddd;padding:6px">${c}</td>`).join("")}</tr>`
            ).join("")}
          </tbody>
        </table>`;
    } else {
      title = "Sales Balance Report";
      tableHtml = `<h2>${title}</h2>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr style="background:#f2f2f2">
            ${salesExportHeaders.map((h) => `<th style="border:1px solid #ddd;padding:6px">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${salesExportRows(filteredDataSales).map((row) =>
              `<tr>${row.map((c) => `<td style="border:1px solid #ddd;padding:6px">${c}</td>`).join("")}</tr>`
            ).join("")}
          </tbody>
        </table>`;
    }

    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial,sans-serif}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f2f2f2}</style></head><body>${tableHtml}</body></html>`);
    w.document.close(); w.focus(); w.print(); w.close();
    toast.success("Print dialog opened!", 2000, "topRight");
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      if (activeTab === "retailers-balance") {
        doc.text("Retailers Balance Report", 14, 10);
        autoTable(doc, {
          head: [retailerExportHeaders],
          body: retailerExportRows(filteredDataRetailer),
          startY: 20,
          styles: { fontSize: 7 },
        });
        doc.save("retailers_balance_report.pdf");
      } else {
        doc.text("Sales Balance Report", 14, 10);
        autoTable(doc, {
          head: [salesExportHeaders],
          body: salesExportRows(filteredDataSales),
          startY: 20,
        });
        doc.save("sales_balance_report.pdf");
      }
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch {
      toast.error("Failed to export PDF", 3000, "topRight");
    }
  };

  // ── CSV ───────────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const isRetailer = activeTab === "retailers-balance";
    const headers  = isRetailer ? retailerExportHeaders : salesExportHeaders;
    const rows     = isRetailer ? retailerExportRows(filteredDataRetailer) : salesExportRows(filteredDataSales);
    const filename = isRetailer ? "retailers_balance.csv" : "sales_balance.csv";

    const csv = [headers.join(","), ...rows.map((r) => r.map((f) => `"${f}"`).join(","))].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    link.download = filename;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success("CSV exported successfully!", 3000, "topRight");
  };

  // ── Excel ─────────────────────────────────────────────────────────────────
  const exportExcel = () => {
    try {
      const isRetailer = activeTab === "retailers-balance";
      const wsData = isRetailer
        ? [retailerExportHeaders, ...retailerExportRows(filteredDataRetailer)]
        : [salesExportHeaders,    ...salesExportRows(filteredDataSales)];
      const filename  = isRetailer ? "retailers_balance.xlsx" : "sales_balance.xlsx";
      const sheetName = isRetailer ? "Retailers Balance"       : "Sales Balance";

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), sheetName);
      XLSX.writeFile(wb, filename);
      toast.success("Excel exported successfully!", 3000, "topRight");
    } catch {
      toast.error("Failed to export Excel", 3000, "topRight");
    }
  };

  // ── Copy ──────────────────────────────────────────────────────────────────
  const copyToClipboard = () => {
    const isRetailer = activeTab === "retailers-balance";
    const headers = isRetailer ? retailerExportHeaders : salesExportHeaders;
    const rows    = isRetailer ? retailerExportRows(filteredDataRetailer) : salesExportRows(filteredDataSales);
    const text    = [headers, ...rows].map((r) => r.map((f) => `"${f}"`).join("\t")).join("\n");

    navigator.clipboard?.writeText(text)
      .then(() => toast.success("Data copied to clipboard!", 3000, "topRight"))
      .catch(() => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Copied!", 2000, "topRight");
      });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}

      <div className="container-fluid">
        <h6 className="card-title m-2">White Sheet Balance</h6>

        {/* Tabs */}
        <div className="card shadow-sm border-0 rounded-3">
          <div className="row p-2">
            <div className="col-auto">
              {[["sales-balance", "Sales Balance"], ["retailers-balance", "Retailers Balance"]].map(([tab, label]) => (
                <button
                  key={tab}
                  className={`btn btn-link text-decoration-none px-3 ms-3 ${
                    activeTab === tab ? "text-white border-bottom bg-primary" : "text-dark"
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  <span style={{ fontSize: "16px" }}>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card shadow-sm mt-2">
          <div className="card-body" style={{ backgroundColor: "rgb(199, 241, 241)" }}>

            {/* Toolbar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <label className="form-label text-dark me-2 mb-0">Show :</label>
                <select className="form-control" value={pageSize} onChange={handlePageSizeChange}>
                  {[5, 10, 20, 100, 200, 500, 1000].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="d-flex justify-content-center mb-2 mb-md-0 flex-wrap">
                {[["Print", handlePrint], ["PDF", exportPDF], ["Copy", copyToClipboard], ["CSV", exportCSV], ["Excel", exportExcel]].map(([lbl, fn]) => (
                  <button key={lbl} className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0" onClick={fn}>{lbl}</button>
                ))}
              </div>

              <div className="search-container w-25">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            <div className="card p-2">

              {/* ── Retailers Balance Tab ───────────────────────────── */}
              {activeTab === "retailers-balance" ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover w-100">
                    <thead className="bg-light">
                      <tr>
                        <th>S.No</th>
                        <th>Retailer No</th>
                        <th>Retailer ID</th>
                        <th>Date</th>
                        <th>Retailer Name</th>
                        <th>Items</th>
                        <th>Return Type</th>
                        <th>Total (₹) <span className="text-muted">/</span> Total Pure (g)</th>
                        <th>Paid (₹) <span className="text-muted">/</span> Returned (g)</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center">
                            No matching records found for today.
                          </td>
                        </tr>
                      ) : (
                        paginatedData.map((item, index) => (
                          <tr key={item.retailer_id + index}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>{item.retailer_id}</td>
                            <td>{item.supplier_id}</td>
                            <td>{formatDate(item.retailer_date)}</td>
                            <td>{item.retailer_details?.name || "-"}</td>
                            <td>{item.items?.length || 0}</td>
                            <td>
                              <span className={`badge ${item.return_type === "Amount" ? "bg-success" : "bg-primary"}`}>
                                {item.return_type || "-"}
                              </span>
                            </td>
                            <td>{fmtTotal(item)}</td>
                            <td>{fmtPaid(item)}</td>
                            <td>
                              <span className={`fw-semibold ${parseFloat(item.close || 0) > 0 ? "text-danger" : "text-success"}`}>
                                {fmtBalance(item)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}

                      {/* Amount-type total row */}
                      {amountRows.length > 0 && (
                        <tr className="table-success fw-bold">
                          <td colSpan="7" className="text-end">
                            Amount Returns Total ({amountRows.length} records)
                          </td>
                          <td>₹{totalAmtValue}</td>
                          <td>₹{totalAmtPaid}</td>
                          <td>₹{totalAmtBalance}</td>
                        </tr>
                      )}

                      {/* Pure-type total row */}
                      {pureRows.length > 0 && (
                        <tr className="table-primary fw-bold text-white">
                          <td colSpan="7" className="text-end">
                            Pure Returns Total ({pureRows.length} records)
                          </td>
                          <td>{totalPureWt} g</td>
                          <td>{totalPureRet} g</td>
                          <td>{totalPureBal} g</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              ) : (
              /* ── Sales Balance Tab ─────────────────────────────────── */
                <div className="table-responsive">
                  <table className="table table-striped table-hover w-100">
                    <thead className="bg-light">
                      <tr>
                        <th>S.No</th>
                        <th>Sales ID</th>
                        <th>Sales Date</th>
                        <th>Category</th>
                        <th>Item Name</th>
                        <th>Customer ID</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No matching records found for today.
                          </td>
                        </tr>
                      ) : (
                        paginatedData.map((item, index) => (
                          <tr key={item.sales_no}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>{item.sales_no}</td>
                            <td>{formatDate(item.sales_date)}</td>
                            <td>{item.item_list[0]?.category || "-"}</td>
                            <td>
                              {item.item_list[0]?.item_name_english
                                ? `${item.item_list[0].item_name_english}${item.item_list[0].item_name_tamil ? " / " + item.item_list[0].item_name_tamil : ""}`
                                : item.item_list[0]?.item || "-"}
                            </td>
                            <td>{item.customer_id}</td>
                            <td>{item.total}</td>
                            <td>{item.paid}</td>
                            <td>{item.balance}</td>
                          </tr>
                        ))
                      )}
                      {paginatedData.length > 0 && (
                        <tr className="table-warning fw-bold">
                          <td colSpan="6" className="text-end">Total</td>
                          <td>₹{filteredDataSales.reduce((s, i) => s + parseFloat(i.total   || 0), 0).toFixed(2)}</td>
                          <td>₹{filteredDataSales.reduce((s, i) => s + parseFloat(i.paid    || 0), 0).toFixed(2)}</td>
                          <td>₹{filteredDataSales.reduce((s, i) => s + parseFloat(i.balance || 0), 0).toFixed(2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="row mt-2">
                <div className="col-lg-6 d-flex align-items-center">
                  {dataToDisplay.length > 0 && (
                    <p className="mb-0">
                      Showing {(currentPage - 1) * pageSize + 1} to{" "}
                      {Math.min(currentPage * pageSize, dataToDisplay.length)} of{" "}
                      {dataToDisplay.length} entries
                    </p>
                  )}
                </div>
                <div className="col-lg-6">
                  <nav>
                    <ul className="pagination justify-content-end">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(1)}>««</button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>«</button>
                      </li>
                      {renderPageNumbers()}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>»</button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(totalPages)}>»»</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WhitePageBalance;